import { useState } from 'react';
import Navigation from '../../components/feature/Navigation';
import Footer from '../../components/feature/Footer';
import ScrollAnimation from '../../components/base/ScrollAnimation';
import Button from '../../components/base/Button';
import { useSEO, generateLocalBusinessSchema, generateBreadcrumbSchema } from '../../utils/seo';

export default function ContactPage() {
  useSEO({
    title: 'Contact U.B.K Window Tint Services - Baltimore MD | (443) 800-0348',
    description: 'Contact U.B.K Window Tint Services in Baltimore, MD. Request a free quote for window tinting, mobile service, or vehicle decals. Call (443) 800-0348 or visit us at 313 Nottingham Road.',
    keywords: 'contact window tinting Baltimore, free quote, appointment booking, Baltimore tint shop',
    canonical: '/contact',
    schema: {
      '@context': 'https://schema.org',
      '@graph': [
        generateLocalBusinessSchema(),
        generateBreadcrumbSchema([
          { name: 'Home', url: '/' },
          { name: 'Contact', url: '/contact' },
        ]),
      ],
    },
  });

  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    service: '',
    tintType: '',
    mobileAddress: '',
    vehicleYear: '',
    vehicleMake: '',
    vehicleModel: '',
    message: '',
  });

  const [submitStatus, setSubmitStatus] = useState<'idle' | 'submitting' | 'success' | 'error'>('idle');

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitStatus('submitting');

    try {
      const formElement = e.target as HTMLFormElement;
      const formDataToSend = new FormData(formElement);
      
      const response = await fetch('https://readdy.ai/api/form/d4fpjuneq0hdrsdob2qg', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: new URLSearchParams(formDataToSend as any).toString(),
      });

      if (response.ok) {
        setSubmitStatus('success');
        setFormData({
          name: '',
          email: '',
          phone: '',
          service: '',
          tintType: '',
          mobileAddress: '',
          vehicleYear: '',
          vehicleMake: '',
          vehicleModel: '',
          message: '',
        });
      } else {
        setSubmitStatus('error');
      }
    } catch (error) {
      setSubmitStatus('error');
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });
  };

  const isMobileTinting = formData.service === 'mobile-tinting';

  return (
    <div className="min-h-screen">
      <Navigation />
      <main className="pt-24 max-md:pt-20">
        {/* Hero Section */}
        <section className="relative pt-12 md:pt-16 pb-8 md:pb-12 bg-gradient-to-br from-secondary via-gray-800 to-secondary overflow-hidden">
          <div className="absolute inset-0 opacity-10">
            <div className="absolute inset-0" style={{
              backgroundImage: 'linear-gradient(45deg, transparent 48%, white 49%, white 51%, transparent 52%)',
              backgroundSize: '20px 20px',
            }}></div>
          </div>
          
          <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
            <ScrollAnimation animation="fade-in">
              <h1 className="text-3xl md:text-5xl lg:text-6xl font-bold text-white mb-3 md:mb-6 leading-tight">
                Get in Touch
              </h1>
              <p className="text-sm md:text-xl text-gray-300 max-w-3xl mx-auto">
                Ready to upgrade your vehicle? Contact us for a free quote and consultation. We're here to answer all your questions.
              </p>
            </ScrollAnimation>
          </div>
        </section>

        {/* Contact Form & Info Section */}
        <section id="quote-section" className="py-8 md:py-24 bg-gradient-to-b from-white to-gray-50 scroll-mt-20">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 md:gap-12">
              {/* Contact Form */}
              <ScrollAnimation animation="slide-in-left">
                <div className="bg-white rounded-2xl shadow-xl p-4 md:p-8">
                  <h2 className="text-xl md:text-3xl font-bold text-secondary mb-3 md:mb-6">Request a Quote</h2>
                  <form onSubmit={handleSubmit} className="space-y-3 md:space-y-6" data-readdy-form id="request-appointment-form">
                    <div>
                      <label htmlFor="name" className="block text-xs md:text-sm font-semibold text-gray-700 mb-1 md:mb-2">
                        Full Name *
                      </label>
                      <input
                        type="text"
                        id="name"
                        name="name"
                        required
                        value={formData.name}
                        onChange={handleChange}
                        className="w-full px-3 md:px-4 py-2 md:py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent transition-all text-xs md:text-sm"
                        placeholder="John Doe"
                      />
                    </div>

                    <div>
                      <label htmlFor="email" className="block text-xs md:text-sm font-semibold text-gray-700 mb-1 md:mb-2">
                        Email Address *
                      </label>
                      <input
                        type="email"
                        id="email"
                        name="email"
                        required
                        value={formData.email}
                        onChange={handleChange}
                        className="w-full px-3 md:px-4 py-2 md:py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent transition-all text-xs md:text-sm"
                        placeholder="john@example.com"
                      />
                    </div>

                    <div>
                      <label htmlFor="phone" className="block text-xs md:text-sm font-semibold text-gray-700 mb-1 md:mb-2">
                        Phone Number *
                      </label>
                      <input
                        type="tel"
                        id="phone"
                        name="phone"
                        required
                        value={formData.phone}
                        onChange={handleChange}
                        className="w-full px-3 md:px-4 py-2 md:py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent transition-all text-xs md:text-sm"
                        placeholder="(443) 800-0348"
                      />
                    </div>

                    <div>
                      <label htmlFor="service" className="block text-xs md:text-sm font-semibold text-gray-700 mb-1 md:mb-2">
                        Service Interested In *
                      </label>
                      <select
                        id="service"
                        name="service"
                        required
                        value={formData.service}
                        onChange={handleChange}
                        className="w-full px-3 md:px-4 py-2 md:py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent transition-all pr-8 text-xs md:text-sm"
                      >
                        <option value="">Select a service</option>
                        <option value="window-tinting">Window Tinting</option>
                        <option value="mobile-tinting">Mobile Tinting</option>
                        <option value="commercial-tinting">Commercial Tinting</option>
                        <option value="headlight-taillight-tinting">Headlight & Taillight Tinting</option>
                        <option value="precut-kits">Pre-Cut Kits</option>
                        <option value="vehicle-decals">Vehicle Decals</option>
                      </select>
                    </div>

                    {(formData.service === 'window-tinting' || formData.service === 'mobile-tinting') && (
                      <div>
                        <label htmlFor="tintType" className="block text-xs md:text-sm font-semibold text-gray-700 mb-1 md:mb-2">
                          Tint Type *
                        </label>
                        <select
                          id="tintType"
                          name="tintType"
                          required
                          value={formData.tintType}
                          onChange={handleChange}
                          className="w-full px-3 md:px-4 py-2 md:py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent transition-all pr-8 text-xs md:text-sm"
                        >
                          <option value="">Select tint type</option>
                          <option value="ceramic">Ceramic Tint</option>
                          <option value="standard">Standard Tint</option>
                        </select>
                      </div>
                    )}

                    {isMobileTinting && (
                      <div>
                        <label htmlFor="mobileAddress" className="block text-xs md:text-sm font-semibold text-gray-700 mb-1 md:mb-2">
                          Mobile Service Address *
                        </label>
                        <input
                          type="text"
                          id="mobileAddress"
                          name="mobileAddress"
                          required
                          value={formData.mobileAddress}
                          onChange={handleChange}
                          className="w-full px-3 md:px-4 py-2 md:py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent transition-all text-xs md:text-sm"
                          placeholder="123 Main St, Baltimore, MD 21229"
                        />
                      </div>
                    )}

                    <div className="grid grid-cols-1 md:grid-cols-3 gap-2 md:gap-4">
                      <div>
                        <label htmlFor="vehicleYear" className="block text-xs md:text-sm font-semibold text-gray-700 mb-1 md:mb-2">
                          Vehicle Year *
                        </label>
                        <input
                          type="text"
                          id="vehicleYear"
                          name="vehicleYear"
                          required
                          value={formData.vehicleYear}
                          onChange={handleChange}
                          className="w-full px-3 md:px-4 py-2 md:py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent transition-all text-xs md:text-sm"
                          placeholder="2023"
                        />
                      </div>
                      <div>
                        <label htmlFor="vehicleMake" className="block text-xs md:text-sm font-semibold text-gray-700 mb-1 md:mb-2">
                          Vehicle Make *
                        </label>
                        <input
                          type="text"
                          id="vehicleMake"
                          name="vehicleMake"
                          required
                          value={formData.vehicleMake}
                          onChange={handleChange}
                          className="w-full px-3 md:px-4 py-2 md:py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent transition-all text-xs md:text-sm"
                          placeholder="Tesla"
                        />
                      </div>
                      <div>
                        <label htmlFor="vehicleModel" className="block text-xs md:text-sm font-semibold text-gray-700 mb-1 md:mb-2">
                          Vehicle Model *
                        </label>
                        <input
                          type="text"
                          id="vehicleModel"
                          name="vehicleModel"
                          required
                          value={formData.vehicleModel}
                          onChange={handleChange}
                          className="w-full px-3 md:px-4 py-2 md:py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent transition-all text-xs md:text-sm"
                          placeholder="Model 3"
                        />
                      </div>
                    </div>

                    <div>
                      <label htmlFor="message" className="block text-xs md:text-sm font-semibold text-gray-700 mb-1 md:mb-2">
                        Additional Details
                      </label>
                      <textarea
                        id="message"
                        name="message"
                        rows={3}
                        value={formData.message}
                        onChange={handleChange}
                        maxLength={500}
                        className="w-full px-3 md:px-4 py-2 md:py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent transition-all resize-none text-xs md:text-sm"
                        placeholder="Tell us about your project..."
                      ></textarea>
                      <p className="text-xs text-gray-500 mt-1">Maximum 500 characters</p>
                    </div>

                    {submitStatus === 'success' && (
                      <div className="bg-green-50 border border-green-200 text-green-800 px-3 md:px-4 py-2 md:py-3 rounded-lg text-xs md:text-sm">
                        Thank you for your request! We will contact you shortly.
                      </div>
                    )}

                    {submitStatus === 'error' && (
                      <div className="bg-red-50 border border-red-200 text-red-800 px-3 md:px-4 py-2 md:py-3 rounded-lg text-xs md:text-sm">
                        There was an error submitting your request. Please try again or call us directly.
                      </div>
                    )}

                    <Button type="submit" size="lg" fullWidth disabled={submitStatus === 'submitting'} className="text-xs md:text-base py-2 md:py-3">
                      <i className="ri-send-plane-line mr-1 md:mr-2"></i>
                      {submitStatus === 'submitting' ? 'Sending...' : 'Send Request'}
                    </Button>
                  </form>
                </div>
              </ScrollAnimation>

              {/* Contact Information */}
              <ScrollAnimation animation="slide-in-right">
                <div className="space-y-4 md:space-y-8">
                  {/* Contact Cards */}
                  <div className="bg-white rounded-2xl shadow-xl p-4 md:p-8">
                    <h3 className="text-lg md:text-2xl font-bold text-secondary mb-3 md:mb-6">Contact Information</h3>
                    
                    <div className="space-y-3 md:space-y-6">
                      <div className="flex items-start space-x-2 md:space-x-4">
                        <div className="w-8 h-8 md:w-12 md:h-12 bg-primary rounded-full flex items-center justify-center flex-shrink-0">
                          <i className="ri-phone-line text-white text-sm md:text-xl"></i>
                        </div>
                        <div>
                          <h4 className="font-semibold text-secondary mb-0.5 md:mb-1 text-xs md:text-base">Phone</h4>
                          <button 
                            onClick={() => window.location.href = 'tel:4438000348'}
                            className="text-sm md:text-lg text-primary hover:text-accent transition-colors whitespace-nowrap cursor-pointer"
                          >
                            (443) 800-0348
                          </button>
                          <p className="text-xs md:text-sm text-gray-600 mt-0.5 md:mt-1">Mon-Sat: 8am - 5pm, Sun: 8am - 12pm</p>
                        </div>
                      </div>

                      <div className="flex items-start space-x-2 md:space-x-4">
                        <div className="w-8 h-8 md:w-12 md:h-12 bg-primary rounded-full flex items-center justify-center flex-shrink-0">
                          <i className="ri-mail-line text-white text-sm md:text-xl"></i>
                        </div>
                        <div>
                          <h4 className="font-semibold text-secondary mb-0.5 md:mb-1 text-xs md:text-base">Email</h4>
                          <a href="mailto:ubkwindowtintservices@gmail.com" className="text-primary hover:text-accent transition-colors break-all text-xs md:text-base">
                            ubkwindowtintservices@gmail.com
                          </a>
                          <p className="text-xs md:text-sm text-gray-600 mt-0.5 md:mt-1">We respond within 24 hours</p>
                        </div>
                      </div>

                      <div className="flex items-start space-x-2 md:space-x-4">
                        <div className="w-8 h-8 md:w-12 md:h-12 bg-primary rounded-full flex items-center justify-center flex-shrink-0">
                          <i className="ri-map-pin-line text-white text-sm md:text-xl"></i>
                        </div>
                        <div>
                          <h4 className="font-semibold text-secondary mb-0.5 md:mb-1 text-xs md:text-base">Location</h4>
                          <p className="text-gray-700 text-xs md:text-base">313 Nottingham Road</p>
                          <p className="text-gray-700 text-xs md:text-base">Baltimore, Maryland 21229</p>
                          <p className="text-xs md:text-sm text-gray-600 mt-0.5 md:mt-1">Serving Baltimore & surrounding areas</p>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Hours Card */}
                  <div className="bg-gradient-to-br from-primary to-accent rounded-2xl shadow-xl p-4 md:p-8 text-white">
                    <h3 className="text-lg md:text-2xl font-bold mb-3 md:mb-6">Business Hours</h3>
                    <div className="space-y-2 md:space-y-3">
                      <div className="flex justify-between items-center pb-2 md:pb-3 border-b border-white/20">
                        <span className="font-medium text-xs md:text-base">Monday - Saturday</span>
                        <span className="font-bold whitespace-nowrap text-xs md:text-base">8:00 AM - 5:00 PM</span>
                      </div>
                      <div className="flex justify-between items-center pb-2 md:pb-3 border-b border-white/20">
                        <span className="font-medium text-xs md:text-base">Sunday</span>
                        <span className="font-bold whitespace-nowrap text-xs md:text-base">8:00 AM - 12:00 PM</span>
                      </div>
                      <div className="bg-white/20 rounded-lg p-2 md:p-4 mt-2 md:mt-4">
                        <p className="font-bold text-center text-xs md:text-base">BY APPOINTMENT ONLY</p>
                        <p className="text-xs md:text-sm text-center mt-1 md:mt-2 opacity-90">Call ahead to schedule your service</p>
                      </div>
                    </div>
                  </div>

                  {/* Special Offer */}
                  <div className="bg-secondary rounded-2xl shadow-xl p-4 md:p-8 text-white">
                    <div className="text-center">
                      <i className="ri-gift-line text-accent text-3xl md:text-5xl mb-2 md:mb-4 w-8 h-8 md:w-12 md:h-12 flex items-center justify-center mx-auto"></i>
                      <h3 className="text-lg md:text-2xl font-bold mb-1.5 md:mb-3">Special Offer!</h3>
                      <p className="text-base md:text-xl mb-1 md:mb-2">Get 10% Off</p>
                      <p className="text-gray-300 mb-2 md:mb-4 text-xs md:text-base">Mention this website when booking your appointment</p>
                      <div className="bg-accent text-white px-3 md:px-6 py-1 md:py-2 rounded-full inline-block font-bold text-xs md:text-base">
                        Limited Time Offer
                      </div>
                    </div>
                  </div>
                </div>
              </ScrollAnimation>
            </div>
          </div>
        </section>

        {/* Map Section */}
        <section className="py-8 md:py-24 bg-white">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <ScrollAnimation animation="fade-in">
              <div className="text-center mb-6 md:mb-12">
                <h2 className="text-2xl md:text-4xl lg:text-5xl font-bold text-secondary mb-2 md:mb-4">
                  Find Us
                </h2>
                <p className="text-sm md:text-xl text-gray-600">
                  Serving Baltimore and surrounding areas with mobile service available
                </p>
              </div>
            </ScrollAnimation>

            <ScrollAnimation animation="slide-up">
              <div className="rounded-2xl overflow-hidden shadow-2xl mb-4 md:mb-8">
                <iframe
                  src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3087.5234!2d-76.6544!3d39.2845!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x89c81bc0a5e1e5e5%3A0x1234567890abcdef!2s313%20Nottingham%20Rd%2C%20Baltimore%2C%20MD%2021229%2C%20USA!5e0!3m2!1sen!2sus!4v1234567890123"
                  width="100%"
                  height="300"
                  style={{ border: 0 }}
                  allowFullScreen
                  loading="lazy"
                  referrerPolicy="no-referrer-when-downgrade"
                  title="U.B.K Window Tint Services Location - 313 Nottingham Road, Baltimore, MD 21229"
                  className="md:h-[450px]"
                ></iframe>
              </div>
              <div className="text-center">
                <a 
                  href="#quote-section"
                  onClick={(e) => {
                    e.preventDefault();
                    const element = document.getElementById('quote-section');
                    if (element) {
                      const offset = 80;
                      const elementPosition = element.getBoundingClientRect().top;
                      const offsetPosition = elementPosition + window.pageYOffset - offset;
                      window.scrollTo({
                        top: offsetPosition,
                        behavior: 'smooth'
                      });
                    }
                  }}
                >
                  <Button size="lg" className="text-xs md:text-base py-2 md:py-3">
                    <i className="ri-calendar-line mr-1 md:mr-2"></i>
                    Request Appointment
                  </Button>
                </a>
              </div>
            </ScrollAnimation>
          </div>
        </section>

        {/* FAQ Section */}
        <section className="py-8 md:py-24 bg-gray-50">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
            <ScrollAnimation animation="fade-in">
              <div className="text-center mb-8 md:mb-16">
                <h2 className="text-2xl md:text-4xl lg:text-5xl font-bold text-secondary mb-2 md:mb-4">
                  Quick Questions
                </h2>
                <p className="text-sm md:text-xl text-gray-600">
                  Common questions about our services
                </p>
              </div>
            </ScrollAnimation>

            <div className="space-y-2 md:space-y-4">
              {[
                {
                  question: 'Do you offer same-day service?',
                  answer: 'Yes! We offer same-day service for most vehicles when scheduled in advance. Call us to check availability.',
                },
                {
                  question: 'What payment methods do you accept?',
                  answer: 'We accept cash, all major credit cards, and digital payments. Payment is due upon completion of service.',
                },
                {
                  question: 'Do you offer mobile service?',
                  answer: 'Absolutely! We bring our professional equipment to your location anywhere in Baltimore and surrounding areas.',
                },
                {
                  question: 'How long does installation take?',
                  answer: 'Most vehicles take 2-4 hours for complete window tinting. We can provide a more accurate estimate based on your specific vehicle.',
                },
              ].map((faq, index) => (
                <ScrollAnimation key={index} animation="slide-up" delay={index * 50}>
                  <details className="bg-white rounded-xl shadow-md overflow-hidden group">
                    <summary className="px-3 md:px-6 py-3 md:py-5 cursor-pointer list-none flex items-center justify-between hover:bg-gray-50 transition-colors">
                      <h3 className="text-sm md:text-lg font-semibold text-secondary pr-4">{faq.question}</h3>
                      <i className="ri-arrow-down-s-line text-lg md:text-2xl text-primary group-open:rotate-180 transition-transform w-5 h-5 md:w-6 md:h-6 flex items-center justify-center flex-shrink-0"></i>
                    </summary>
                    <div className="px-3 md:px-6 pb-3 md:pb-5 text-xs md:text-base text-gray-600 leading-relaxed">
                      {faq.answer}
                    </div>
                  </details>
                </ScrollAnimation>
              ))}
            </div>
          </div>
        </section>

        <Footer />
      </main>
    </div>
  );
}
